import * as React from 'react';
import {
    AcceptIcon,
    AddIcon,
    ArchiveIcon,
    Button,
    Card,
    CloseIcon,
    Dialog,
    EditIcon,
    Flex,
    FlexItem,
    Image as RNImage,
    Text,
    TrashCanIcon
} from "@fluentui/react-northstar";
import EditInput from "../Utils/EditInput";
import SharepointPicker from "../Utils/SharepointPicker";
import {blobsRootFolderName, deleteBlobImage, updateBlobImage} from "../../apis/blobImageApi";
import FolderNameSelectorDialog from "../Dialogs/FolderNameSelectorDialog";
import {ImageGallery} from '../../Interfaces/ImageGallery';
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";

interface IPictureViewerProps {
    // Image
    image?: ImageGallery,
    // On delete image
    onDelete: (imageName: string) => Promise<void>,
    // On update image
    onUpdateImage: (oldName: string, newName: string) => Promise<void>,
    // On create folder
    onCreateFolder: (folderPath: string) => Promise<void>,
    // Available items
    items: Array<string>,
    // Is uploading folder
    uploadingFolder: boolean
}

interface IPictureViewerState {
    // Image name
    name: string,
    // Image old name
    oldName: string,
    // Image width
    width: number,
    // Image height
    height: number,
    // Image extension
    extension: string,
    // Deleting image
    deletingImage: boolean,
    // Editing image name
    editingName: boolean,
    // Updating image
    updatingImage: boolean,
    // Image path
    path: string,
    // Show confirm delete dialog
    showConfirmDeleteDialog: boolean,
    // Show move image dialog
    showMoveImageDialog: boolean,
    // Show folder creator
    showFolderCreator: boolean,
    // New image location in case of moving image
    newImageLocation: string,
    // Forbidden folders names
    forbiddenFoldersNames: Array<string>,
}

/**
 * Default state
 */
const defaultState = {
    name: "",
    oldName: "",
    width: 0,
    height: 0,
    extension: "",
    path: "",
    deletingImage: false,
    editingName: false,
    updatingImage: false,
    showConfirmDeleteDialog: false,
    showMoveImageDialog: false,
    showFolderCreator: false,
    newImageLocation: "",
    forbiddenFoldersNames: new Array<string>()
} as IPictureViewerState;

/**
 * Picture viewer
 */
class PictureViewer extends React.Component<IPictureViewerProps, IPictureViewerState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    /**
     * Constructor
     * @param props
     */
    constructor(props: IPictureViewerProps) {
        super(props);
        this.state = {...defaultState}
    }

    /**
     * Component initialization
     */
    componentDidMount() {
        this.loadImage();
    }

    /**
     * Allow component to render
     * @param nextProps next props
     */
    shouldComponentUpdate(nextProps: Readonly<IPictureViewerProps>): boolean {
        return !!this.props.image || !!nextProps.image;
    }

    /**
     * Component has updated
     * @param prevProps previous props
     * @param prevState previous state
     */
    componentDidUpdate(prevProps: Readonly<IPictureViewerProps>, prevState: Readonly<IPictureViewerState>) {
        // If an image has been selected by user
        if (prevProps.image !== this.props.image && !!this.props.image)
            this.loadImage();

        // Update image blob on renaming name
        if (prevState.editingName && !this.state.editingName)
            this.renameImage();
    }

    /**
     * Load props image
     */
    loadImage = () => {
        if (!!this.props.image) {
            let newImageLocation = this.props.image.imageLink.substring(0, this.props.image.imageLink.lastIndexOf("/") + 1);
            let path = newImageLocation.replace(blobsRootFolderName, "");
            let imageName = this.props.image.imageLink.substring(this.props.image.imageLink.lastIndexOf("/") + 1);
            let imageExtension = imageName.split(".")[1]?.toUpperCase();
            imageName = imageName.split(".")[0];
            const img = new Image();
            img.onload = () => {
                this.setState({
                    name: imageName,
                    oldName: imageName,
                    width: img.width,
                    height: img.height,
                    extension: imageExtension,
                    path: path,
                    newImageLocation: newImageLocation
                });
            }
            img.src = this.props.image.content;
        }
    }

    /**
     * Delete an image
     */
    deleteImage = () => {
        this.setState({deletingImage: true});
        const imagePath = this.state.path + this.state.name + "." + this.state.extension.toLowerCase();
        deleteBlobImage(imagePath)
            .then(async success => {
                if (success) {
                    await this.props.onDelete(imagePath);
                    this.setState({...defaultState});
                } else {
                    console.error("Can't delete image")
                    this.setState({...defaultState});
                }
            });
    }

    /**
     * Rename image
     */
    renameImage = () => {
        const oldImageName = this.state.path + this.state.oldName + "." + this.state.extension.toLowerCase();
        const newImageName = this.state.path + this.state.name + "." + this.state.extension.toLowerCase();
        // Do nothing if same name
        if (oldImageName === newImageName) return;

        this.setState({updatingImage: true});
        updateBlobImage(oldImageName, newImageName)
            .then(async success => {
                if (success) await this.props.onUpdateImage(oldImageName, newImageName);
                else console.error("Can't update image")
                this.setState({updatingImage: false});
            })
    }

    /**
     * Format folder title
     * @param path folder complete path
     * @param onlyName keep only folder name
     */
    formatFolderTitle = (path: string, onlyName: boolean = true) => {
        if (path === blobsRootFolderName) return languageObject.translate("MyImages");
        path = path.replace(blobsRootFolderName, languageObject.translate("MyImages") + "/")
        if (onlyName) path = path.substring(path.lastIndexOf("/") + 1);
        return path;
    }

    /**
     * Create a new subfolder
     */
    createSubFolder = (folderName: string) => {
        if (!!this.props.image?.imageLink) {
            const subFolderName = this.state.newImageLocation + (this.state.newImageLocation.endsWith("/") ? "" : "/") + folderName;
            this.props.onCreateFolder(subFolderName).then(() => {
                this.setState({showFolderCreator: false});
            })
        }
    }

    /**
     * Move image to a new location
     */
    moveImage = () => {
        this.setState({updatingImage: true});
        return new Promise<void>((resolve, reject) => {
            if (!this.props.image) {
                reject("Image not available");
                return;
            }
            // Old image name
            const oldImageName = this.props.image.imageLink.replace(blobsRootFolderName, "");
            // New image name
            const newImageName = this.state.newImageLocation.replace(blobsRootFolderName, "") + "/"
                + oldImageName.substring(oldImageName.lastIndexOf("/") + 1);

            // Update image
            updateBlobImage(oldImageName, newImageName)
                .then(async success => {
                    if (success) await this.props.onUpdateImage(oldImageName, newImageName);
                    else console.error("Can't move image")
                    this.setState({updatingImage: false, showMoveImageDialog: false});
                });
        })
    }

    /**
     * Render component
     */
    render() {
        if (!this.props.image) return null;

        return (
            <Card id={"card_picture_details"}>
                <Flex id={"picture_viewer"}>
                    <RNImage id="picture" src={this.props.image.content}/>
                </Flex>
                <Flex fill column>
                    {/* Image name input */}
                    <Flex vAlign={"end"} gap={"gap.small"}>
                        <EditInput
                            edit={this.state.editingName}
                            label={languageObject.translate("Name")}
                            value={this.state.name}
                            max={100}
                            setValue={value => (value.length === 0 || value.match(/^[a-zA-Z0-9-_ ]+$/)) ? this.setState({name: value}) : null}/>
                        {!!this.props.image &&
                            <Button icon={this.state.editingName ? <AcceptIcon outline/> : <EditIcon outline/>}
                                    content={languageObject.translate(this.state.editingName ? "Confirm" : "Edit")}
                                    loading={this.state.updatingImage && !this.state.showMoveImageDialog}
                                    disabled={this.state.updatingImage}
                                    styles={{minWidth: "auto"}}
                                    onClick={() => this.setState({editingName: !this.state.editingName})}/>
                        }
                    </Flex>

                    {/* Image size */}
                    <EditInput disabled label={languageObject.translate("Size")}
                               value={!!this.props.image ? this.state.width + "x" + this.state.height + "px" : ""}/>

                    {/* Image extension*/}
                    <EditInput disabled label={languageObject.translate("Extension")} value={this.state.extension}/>

                    {/* Delete & Move buttons */}
                    {!!this.props.image &&
                        <Flex fill gap={"gap.small"} vAlign={"end"}>
                            <FlexItem push>
                                <Button
                                    onClick={() => this.setState({showMoveImageDialog: true})}
                                    disabled={this.state.updatingImage}
                                    icon={<ArchiveIcon outline/>}
                                    content={languageObject.translate("Move")}
                                />
                            </FlexItem>
                            <Button
                                primary
                                className={this.state.deletingImage || this.state.updatingImage ? "" : "delete_button"}
                                onClick={() => this.setState({showConfirmDeleteDialog: true})}
                                disabled={this.state.deletingImage || this.state.updatingImage}
                                icon={<TrashCanIcon outline size={"medium"}/>}
                                content={languageObject.translate("Delete")}
                            />
                        </Flex>
                    }
                </Flex>
                {this.renderConfirmDeleteDialog()}
                {this.renderMoveDialog()}
                {this.renderFolderCreator()}
            </Card>
        );
    }

    /**
     * Render confirm delete dialog
     */
    renderConfirmDeleteDialog = () => {
        return (
            <Dialog
                closeOnOutsideClick={false}
                styles={{maxWidth: "600px"}}
                open={this.state.showConfirmDeleteDialog}
                confirmButton={{
                    className: this.state.deletingImage || this.state.updatingImage ? "" : "delete_button",
                    content: languageObject.translate("Delete"),
                    icon: <TrashCanIcon outline/>,
                    loading: this.state.deletingImage,
                    disabled: this.state.deletingImage || this.state.updatingImage,
                }}
                onConfirm={this.deleteImage}
                cancelButton={{
                    content: languageObject.translate("Cancel"),
                    icon: <CloseIcon outline/>
                }}
                onCancel={() => this.setState({showConfirmDeleteDialog: false})}
                content={
                    <Text content={languageObject.translate("ConfirmDeleteImage")}/>
                }
                header={languageObject.translate("DeleteImage")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: () => this.setState({showConfirmDeleteDialog: false}),
                }}/>
        )
    }

    /**
     * Render move dialog
     */
    renderMoveDialog = () => {
        // Footer text
        const footerText = !this.state.newImageLocation ?
            languageObject.translate("PleaseSelectFolder")
            :
            <>
                {languageObject.translate("YouHaveSelectedFolder") + " "}
                <Text weight={"semibold"} color={"brand"}>
                    {this.state.newImageLocation === blobsRootFolderName ?
                        languageObject.translate("MyImages") : this.formatFolderTitle(this.state.newImageLocation)}
                </Text>
            </>

        return (
            <Dialog
                closeOnOutsideClick={false}
                styles={{maxWidth: "600px"}}
                open={this.state.showMoveImageDialog}
                confirmButton={{
                    content: languageObject.translate("Move"),
                    icon: <ArchiveIcon outline/>,
                    loading: this.state.updatingImage,
                    disabled: this.state.deletingImage || this.state.updatingImage,
                }}
                onConfirm={this.moveImage}
                cancelButton={{
                    content: languageObject.translate("Cancel"),
                    disabled: this.state.updatingImage,
                    icon: <CloseIcon outline/>
                }}
                onCancel={() => this.setState({showMoveImageDialog: false})}
                content={
                    <Flex fill column>
                        <Flex hAlign={"end"} gap={"gap.medium"} styles={{marginBottom: "15px"}}>
                            {/* Add subfolder button */}
                            <Button icon={<AddIcon outline/>}
                                    disabled={!this.state.newImageLocation || this.state.updatingImage}
                                    content={languageObject.translate("NewSubFolder")}
                                    onClick={() => this.setState({showFolderCreator: true})}/>
                        </Flex>
                        <SharepointPicker
                            disabled={this.state.updatingImage}
                            onlyFolders
                            styles={{height: "300px"}}
                            items={this.props.items}
                            selectedFolder={this.state.newImageLocation}
                            handleSelect={(folder, subfolders) => this.setState({
                                newImageLocation: folder,
                                forbiddenFoldersNames: subfolders ?? new Array<string>()
                            })}
                            noItemsContent={
                                <Flex fill column id={"sharepoint_picker"} hAlign={"center"} vAlign={"center"}>
                                    <Text content={languageObject.translate("NoFolder")}/>
                                </Flex>
                            }
                        />
                        <Flex>
                            <Text weight={"semibold"} content={footerText} truncated/>
                        </Flex>
                    </Flex>
                }
                header={languageObject.translate("MoveImage")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: () => this.setState({showMoveImageDialog: false}),
                }}/>
        )
    }

    /**
     * Render folder creator dialog
     */
    renderFolderCreator = () => {
        return (
            <FolderNameSelectorDialog
                loading={this.props.uploadingFolder}
                show={this.state.showFolderCreator}
                onCreateFolder={folderName => this.createSubFolder(folderName)}
                onHide={() => this.setState({showFolderCreator: false})}
                existingFolders={this.state.forbiddenFoldersNames}
                currentFolder={this.state.newImageLocation}
            />
        )
    }
}

export default PictureViewer